import { useEffect, useState } from 'react';
import { useStore } from 'zustand';

import { SearchResultTab } from './search-result-tab.component';

import { searchHealthResources } from '@/app/(main)/_components/search-modal/_services/search-health-resources.service';
import { AllSearchResults, SearchHealthResourcesResponse, SearchResult, SearchType } from '@/app/(main)/_components/search-modal/search-modal.types';
import { searchHealthResourcesByType } from '@/app/(main)/_components/search-modal/_services/search-health-resources-by-type.service';
import { setFetchedResultsByType, useSearchState } from '@/store/search/search.store';
import { SelectedProcedures } from '@/app/(main)/_components/search-modal/search-modal.types';
import { OverridedUserLocation } from '@/utils/formatters/city/format-place-from-slug.const';
import { getLogger } from '@/logger/logger';
import { InfiniteScroll } from '@/components/ui/infinite-scroll.component';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { SearchModalListEnum } from '@/types/enums/analytics/home/home-analytics.enum';
import { useFeatureFlag } from '@/feature-flags/hooks/use-feature-flag.hook';
import { RESULTS_PAGE_SIZE } from '@/app/(main)/_components/search-modal/_consts/search-results.consts';

interface SearchResultsProps {
  results: SearchHealthResourcesResponse;
  searchType: SearchType;
  searchTerm: string;
  selectLaboratoryProcedure?: (procedure: SelectedProcedures) => void;
  setOpen: (open: boolean) => void;
  locationOverride?: OverridedUserLocation;
}

export const SearchResults = ({
  results,
  searchType,
  searchTerm,
  setOpen,
  selectLaboratoryProcedure,
  locationOverride,
}: SearchResultsProps) => {
  const fetchedResults = useStore(useSearchState, (state) => state?.fetchedResults);
  const { value: newSearchModalDesign } = useFeatureFlag('newSearchModalDesign');

  const chooseInitialDisplayedResults = () => {
    if (fetchedResults) {
      return fetchedResults[searchType as keyof AllSearchResults].results;
    }

    return results.results;
  };

  const chooseInitialPage = () => {
    if (fetchedResults) {
      return Math.ceil(fetchedResults[searchType as keyof AllSearchResults].results.length / RESULTS_PAGE_SIZE);
    }

    return 1;
  };

  const [displayedResults, setDisplayedResults] = useState<SearchResult[]>(chooseInitialDisplayedResults());
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(chooseInitialPage());
  const [hasMore, setHasMore] = useState(displayedResults.length < results.total);

  const fetchPage = (pageNumber: number): Promise<SearchHealthResourcesResponse> =>
    searchType === SearchType.ALL
      ? searchHealthResources(searchTerm, pageNumber, RESULTS_PAGE_SIZE)
      : searchHealthResourcesByType(searchTerm, searchType, pageNumber, RESULTS_PAGE_SIZE);

  const loadMoreResults = async () => {
    if (isLoading || !hasMore) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetchPage(page + 1);

      setDisplayedResults((prev) => [...prev, ...response.results]);
      setFetchedResultsByType(searchType, [...displayedResults, ...response.results], response.total);
      setPage((prevPage) => prevPage + 1);
      setHasMore(displayedResults.length + response.results.length < response.total);
    } catch (error) {
      const logger = getLogger('SearchModal - SearchResults');
      logger.error('Error loading more results', {
        search: { type: searchType, term: searchTerm, page },
        error,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadFirstFullPage = async () => {
    setIsLoading(true);
    try {
      const response = await fetchPage(1);

      setDisplayedResults(response.results);
      setFetchedResultsByType(searchType, response.results, response.total);
      setPage(1);
      setHasMore(response.results.length < response.total);
    } catch (error) {
      const logger = getLogger('SearchModal - SearchResults');
      logger.error('Error loading the first page of results', {
        search: { type: searchType, term: searchTerm },
        error,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const seedIsAPartialPage
      = displayedResults.length < RESULTS_PAGE_SIZE && results.total > displayedResults.length;

    if (seedIsAPartialPage) {
      loadFirstFullPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      {displayedResults.map((result: SearchResult, index: number) => (
        <div
          key={`${result.id}${result.displayName}-${index}`}
          className="hover:bg-gray-50 transition-colors"
        >
          <SearchResultTab
            {...result}
            variant={newSearchModalDesign ? 'compact' : undefined}
            locationOverride={locationOverride}
            searchTerm={searchTerm}
            list={SearchModalListEnum.RESULTS}
            position={index + 1}
            totalResults={results.total}
            setOpen={setOpen}
            selectLaboratoryProcedure={selectLaboratoryProcedure}
          />
          <hr />
        </div>
      ))}

      <InfiniteScroll
        hasMore={hasMore}
        isLoading={isLoading}
        onLoadMore={loadMoreResults}
        data-analytics-event={AnalyticsEnum.SHOW_MORE_RESULTS_PROCEDURES_MODAL}
      />
    </div>
  );
};
